import { useEffect, useState, type ComponentType, type ReactElement, type ReactNode } from 'react';
import {
    Alert,
    Badge,
    Box,
    Button,
    Center,
    Group,
    Loader,
    NumberInput,
    SegmentedControl,
    Select,
    Stack,
    Switch,
    Tabs,
    Text,
    TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import SettingRow from '@/design/SettingRow';
import {
    Activity,
    Bell,
    CalendarClock,
    Clock,
    FileBarChart,
    Gauge,
    History,
    Mail,
    MessageSquare,
    Recycle,
    Send,
    ShieldAlert,
    SlidersHorizontal,
    TrendingUp,
} from 'lucide-react';
import MegaGrid, { type MegaGridColumn } from '@/design/MegaGrid/MegaGrid';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import { SurfaceCard } from '@/design/SurfaceCard';
import DetailDrawer from '@/design/DetailDrawer';
import StatTile from '@/design/StatTile';
import KeyValueTable from '@/design/KeyValueTable';
import { formatTimestamp } from '@/shared/DateFormatting';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { NotificationPreferencesViewModel } from '@/features/notifications/NotificationPreferencesViewModel';
import { NotificationActivityViewModel } from '@/features/notifications/NotificationActivityViewModel';
import type {
    EffectiveNotificationOption,
    EffectiveNotificationPreference,
    NotificationActivityEntry,
    NotificationChannel,
} from '@/features/notifications/NotificationTypes';

const DIGEST_OPTIONS = [
    { value: 'off', label: 'Off' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
];

const DIGEST_TIME_OPTIONS = [
    { value: '08:00', label: '8:00 AM' },
    { value: '12:00', label: '12:00 PM' },
    { value: '17:00', label: '5:00 PM' },
];

/**
 * Icon per notification type. Keyed on the service's `templateType` so the row
 * list itself stays server-driven — an unmapped type falls back to a bell
 * rather than disappearing.
 */
const TEMPLATE_ICONS: Record<string, ComponentType<{ size?: number; color?: string }>> = {
    'license.utilization.threshold': Gauge,
    'license.reclamation.opportunity': Recycle,
    'license.renewal.upcoming': CalendarClock,
    'spend.anomaly.detected': TrendingUp,
    'security.identity.changed': ShieldAlert,
    'system.health.degraded': Activity,
    'report.adoption.weekly': FileBarChart,
};

const STATUS_BADGE_COLOR: Record<string, string> = {
    sent: 'teal',
    partial: 'yellow',
    failed: 'red',
};

const STATUS_LABEL: Record<string, string> = {
    sent: 'Sent',
    partial: 'Partly delivered',
    failed: 'Failed',
};

const CHANNEL_LABEL: Record<string, string> = {
    inApp: 'In-app',
    email: 'Email',
};

function channelsLabel(entry: NotificationActivityEntry): string {
    return entry.channels.map((c) => CHANNEL_LABEL[c] ?? c).join(' · ');
}

function sectionLabel(text: string): ReactElement {
    return (
        <Text
            size="xs"
            fw={700}
            tt="uppercase"
            mb="sm"
            style={{ fontSize: '9px', letterSpacing: '0.08em', color: 'var(--bm-color-text-dimmed)' }}
        >
            {text}
        </Text>
    );
}

function ChannelToggle({
    label,
    checked,
    disabled,
    onChange,
}: {
    label: string;
    checked: boolean;
    disabled: boolean;
    onChange: () => void;
}): ReactElement {
    return (
        <Group gap={8} wrap="nowrap">
            <Switch size="sm" checked={checked} disabled={disabled} onChange={onChange} aria-label={label} />
            <Text size="xs" w={44}>
                {label}
            </Text>
        </Group>
    );
}

/**
 * Renders one tunable from the definition the server sent — the control type,
 * its choices, and its bounds all come from the API, so a new option on a
 * notification type needs no change here.
 */
function OptionControl({
    option,
    disabled,
    onChange,
}: {
    option: EffectiveNotificationOption;
    disabled: boolean;
    onChange: (value: string) => void;
}): ReactElement {
    const label = `${option.label}${option.description ? ` — ${option.description}` : ''}`;

    switch (option.component) {
        case 'select':
            return (
                <Select
                    data={option.choices}
                    value={option.value}
                    onChange={(value) => value !== null && onChange(value)}
                    disabled={disabled}
                    aria-label={label}
                    size="xs"
                    allowDeselect={false}
                />
            );
        case 'number':
            return (
                <NumberInput
                    value={Number(option.value)}
                    onChange={(value) => onChange(String(value))}
                    min={option.min}
                    max={option.max}
                    disabled={disabled}
                    aria-label={label}
                    size="xs"
                />
            );
        case 'switch':
            return (
                <Switch
                    size="sm"
                    checked={option.value === 'true'}
                    onChange={(event) => onChange(String(event.currentTarget.checked))}
                    disabled={disabled}
                    aria-label={label}
                />
            );
        case 'text':
            return (
                <TextInput
                    value={option.value}
                    onChange={(event) => onChange(event.currentTarget.value)}
                    disabled={disabled}
                    aria-label={label}
                    size="xs"
                />
            );
    }
}

/**
 * One notification type's row. Channels the delivery policy marks
 * non-configurable render disabled, so the user never flips a switch that the
 * service is going to refuse.
 */
function PreferenceRow({
    preference,
    last,
    vm,
}: {
    preference: EffectiveNotificationPreference;
    last: boolean;
    vm: NotificationPreferencesViewModel;
}): ReactElement {
    const Icon = TEMPLATE_ICONS[preference.templateType] ?? Bell;
    const active = preference.channels.inApp.enabled || preference.channels.email.enabled;

    const reportFailure = (outcome: { ok: boolean; issue?: { message: string } }): void => {
        if (outcome.ok) return;
        notifications.show({
            title: 'Change not saved',
            message: outcome.issue?.message ?? 'Could not update this notification preference.',
            color: 'red',
        });
    };

    const toggle = async (channel: NotificationChannel): Promise<void> => {
        reportFailure(await vm.toggleChannel(preference.templateType, channel));
    };

    const setOption = async (optionId: string, value: string): Promise<void> => {
        reportFailure(await vm.setOption(preference.templateType, optionId, value));
    };

    return (
        <SettingRow
            title={preference.templateName}
            description={preference.description}
            last={last}
            leading={
                <Icon size={20} color={active ? 'var(--bm-color-text)' : 'var(--bm-color-text-dimmed)'} />
            }
            control={
                <Group gap="md" wrap="wrap">
                    {!preference.userCanFullyOptOut && (
                        <Badge size="sm" variant="light" color="gray">
                            Always on
                        </Badge>
                    )}
                    {preference.options.map((option) => (
                        <Box key={option.id} w={150}>
                            <OptionControl
                                option={option}
                                disabled={!active || vm.isSaving(preference.templateType, option.id)}
                                onChange={(value) => void setOption(option.id, value)}
                            />
                        </Box>
                    ))}
                    <ChannelToggle
                        label="In-app"
                        checked={preference.channels.inApp.enabled}
                        disabled={
                            !preference.channels.inApp.userConfigurable ||
                            vm.isSaving(preference.templateType, 'inApp')
                        }
                        onChange={() => void toggle('inApp')}
                    />
                    <ChannelToggle
                        label="Email"
                        checked={preference.channels.email.enabled}
                        disabled={
                            !preference.channels.email.userConfigurable ||
                            vm.isSaving(preference.templateType, 'email')
                        }
                        onChange={() => void toggle('email')}
                    />
                </Group>
            }
        />
    );
}

function Section({
    title,
    description,
    children,
}: {
    title: string;
    description: string;
    children: ReactNode;
}): ReactElement {
    return (
        <Box>
            <Text size="sm" fw={600}>
                {title}
            </Text>
            <Text size="xs" c="dimmed" mb="sm">
                {description}
            </Text>
            {children}
        </Box>
    );
}

export default function NotificationsSettingsPage(): ReactElement {
    const [tab, setTab] = useState<string | null>('preferences');
    const [selected, setSelected] = useState<NotificationActivityEntry | null>(null);
    const vm = useViewModel(NotificationPreferencesViewModel);
    const activityVm = useViewModel(NotificationActivityViewModel);

    const [inAppEnabled, setInAppEnabled] = useState(true);
    const [emailEnabled, setEmailEnabled] = useState(true);
    const [slackEnabled, setSlackEnabled] = useState(false);
    const [slackWebhook, setSlackWebhook] = useState('https://hooks.slack.com/services/T0000/B0000/XXXX');
    const [digest, setDigest] = useState('daily');
    const [digestTime, setDigestTime] = useState<string | null>('08:00');

    useEffect(() => {
        void vm.load();
    }, [vm]);

    useEffect(() => {
        if (tab === 'activity') void activityVm.loadOnce();
    }, [tab, activityVm]);

    const save = (): void => {
        notifications.show({
            title: 'Delivery settings saved',
            message: 'Your delivery settings have been updated.',
            color: 'green',
        });
    };

    const sentColumns: MegaGridColumn<NotificationActivityEntry>[] = [
        {
            key: 'sentAt',
            header: 'Sent',
            width: 130,
            sortValue: (n) => n.sentAt,
            render: (n) => (
                <Text size="xs" c="dimmed" ff="monospace">
                    {formatTimestamp(n.sentAt)}
                </Text>
            ),
        },
        {
            key: 'type',
            header: 'Notification',
            sortValue: (n) => n.title,
            render: (n) => (
                <Box>
                    <Text size="sm" fw={500}>
                        {n.title}
                    </Text>
                    <Text size="xs" c="dimmed">
                        {n.templateType}
                    </Text>
                </Box>
            ),
        },
        {
            key: 'channels',
            header: 'Channels',
            width: 150,
            render: (n) => (
                <Group gap={6}>
                    {n.channels.map((c) => (
                        <Badge key={c} size="sm" variant="light" color="gray">
                            {CHANNEL_LABEL[c] ?? c}
                        </Badge>
                    ))}
                </Group>
            ),
        },
        {
            key: 'recipients',
            header: 'Recipients',
            width: 110,
            sortValue: (n) => n.recipients,
            render: (n) => (
                <Text size="xs" c="dimmed">
                    {n.recipients}
                </Text>
            ),
        },
        {
            key: 'status',
            header: 'Status',
            width: 120,
            sortValue: (n) => n.status,
            render: (n) => (
                <Badge size="sm" variant="light" color={STATUS_BADGE_COLOR[n.status]}>
                    {STATUS_LABEL[n.status]}
                </Badge>
            ),
        },
    ];

    return (
        <Box>
            <PageHeaderComponent
                title="Notifications"
                subtitle="Choose which alerts you receive, how they're delivered, and review what's been sent."
            />

            <Tabs value={tab} onChange={setTab} keepMounted={false}>
                <Tabs.List mb="xl">
                    <Tabs.Tab value="preferences" leftSection={<SlidersHorizontal size={14} />}>
                        Notifications
                    </Tabs.Tab>
                    <Tabs.Tab value="activity" leftSection={<History size={14} />}>
                        Activity
                    </Tabs.Tab>
                    <Tabs.Tab value="delivery" leftSection={<Send size={14} />}>
                        Delivery
                    </Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="preferences">
                    <SurfaceCard $padding="lg" $static>
                    <Section
                        title="Notification types"
                        description="Choose the channels for each alert. Changes save as you make them; turn both channels off to mute an alert."
                    >
                        {vm.loading ? (
                            <Center py="xl">
                                <Loader size="sm" />
                            </Center>
                        ) : vm.failed ? (
                            <Alert color="red" variant="light">
                                Couldn’t load your notification preferences. Try again shortly.
                            </Alert>
                        ) : vm.preferences.length === 0 ? (
                            <Text size="sm" c="dimmed">
                                No notification types are configured for this tenant yet.
                            </Text>
                        ) : (
                            vm.preferences.map((preference, i) => (
                                <PreferenceRow
                                    key={preference.templateType}
                                    preference={preference}
                                    last={i === vm.preferences.length - 1}
                                    vm={vm}
                                />
                            ))
                        )}
                    </Section>
                    </SurfaceCard>
                </Tabs.Panel>

                <Tabs.Panel value="activity">
                    <SurfaceCard $padding="lg" $static>
                        <Text size="sm" fw={600}>
                            Sent notifications
                        </Text>
                        <Text size="xs" c="dimmed" mb="sm">
                            Every notification this tenant has sent, across all channels.
                        </Text>
                        {activityVm.loading ? (
                            <Center py="xl">
                                <Loader size="sm" />
                            </Center>
                        ) : activityVm.forbidden ? (
                            <Alert color="gray" variant="light">
                                You don’t have permission to view tenant-wide notification activity.
                            </Alert>
                        ) : activityVm.failed ? (
                            <Alert color="red" variant="light">
                                Couldn’t load notification activity. Try again shortly.
                            </Alert>
                        ) : (
                            <MegaGrid
                                persistenceKey="settings-notifications"
                                columns={sentColumns}
                                rows={activityVm.entries}
                                getRowKey={(n) => n.correlationId}
                                onRowClick={(n) => setSelected(n)}
                                searchAccessor={(n) => `${n.title} ${n.templateType}`}
                                searchPlaceholder="Search sent notifications"
                                clientPageSize={10}
                                filters={[
                                    {
                                        key: 'status',
                                        placeholder: 'Status',
                                        allLabel: 'All statuses',
                                        options: [
                                            { label: 'Sent', value: 'sent' },
                                            { label: 'Partly delivered', value: 'partial' },
                                            { label: 'Failed', value: 'failed' },
                                        ],
                                        accessor: (n) => n.status,
                                        width: 160,
                                    },
                                ]}
                            />
                        )}
                    </SurfaceCard>
                </Tabs.Panel>

                <Tabs.Panel value="delivery">
                    <SurfaceCard $padding="lg" $static>
                    <Stack gap="xl">
                        <Alert color="gray" variant="light">
                            Account-wide delivery defaults, Slack, and digest scheduling aren’t connected to
                            the notifications service yet — per-notification channels on the Notifications tab
                            are what currently take effect.
                        </Alert>
                        <Section
                            title="Channels"
                            description="Default channels used for the notifications you're subscribed to."
                        >
                            <SettingRow
                                title="In-app notifications"
                                description="Show alerts in the notification center inside UltraTrace."
                                leading={<Bell size={20} color="var(--bm-color-text-dimmed)" />}
                                control={
                                    <Switch checked={inAppEnabled} onChange={() => setInAppEnabled((v) => !v)} />
                                }
                            />
                            <SettingRow
                                title="Email notifications"
                                description="Send alerts to your account email address."
                                leading={<Mail size={20} color="var(--bm-color-text-dimmed)" />}
                                control={
                                    <Switch checked={emailEnabled} onChange={() => setEmailEnabled((v) => !v)} />
                                }
                            />
                            <SettingRow
                                title="Slack"
                                description="Post alerts to a Slack channel via an incoming webhook."
                                leading={<MessageSquare size={20} color="var(--bm-color-text-dimmed)" />}
                                last
                                control={
                                    <Group gap="md" wrap="nowrap">
                                        <TextInput
                                            value={slackWebhook}
                                            onChange={(e) => setSlackWebhook(e.currentTarget.value)}
                                            disabled={!slackEnabled}
                                            size="xs"
                                            w={320}
                                            placeholder="https://hooks.slack.com/services/…"
                                        />
                                        <Switch
                                            checked={slackEnabled}
                                            onChange={() => setSlackEnabled((v) => !v)}
                                        />
                                    </Group>
                                }
                            />
                        </Section>

                        <Section
                            title="Digest"
                            description="Bundle non-urgent notifications into a single scheduled summary."
                        >
                            <Group gap="xl" align="flex-end" pt="xs">
                                <Box>
                                    <Text size="xs" c="dimmed" mb={6}>
                                        Frequency
                                    </Text>
                                    <SegmentedControl
                                        value={digest}
                                        onChange={setDigest}
                                        data={DIGEST_OPTIONS}
                                    />
                                </Box>
                                <Box>
                                    <Text size="xs" c="dimmed" mb={6}>
                                        Delivery time
                                    </Text>
                                    <Select
                                        data={DIGEST_TIME_OPTIONS}
                                        value={digestTime}
                                        onChange={setDigestTime}
                                        disabled={digest === 'off'}
                                        leftSection={<Clock size={14} />}
                                        w={180}
                                    />
                                </Box>
                            </Group>
                        </Section>
                    </Stack>
                    </SurfaceCard>
                </Tabs.Panel>
            </Tabs>

            {tab === 'delivery' && (
                <Group mt="xl">
                    <Button onClick={save}>Save delivery settings</Button>
                </Group>
            )}

            <DetailDrawer
                opened={selected !== null}
                onClose={() => setSelected(null)}
                size={560}
                avatarSource={selected?.title ?? ''}
                title={selected?.title ?? ''}
                subtitle={selected === null ? '' : `Sent ${formatTimestamp(selected.sentAt)}`}
                stats={
                    selected === null ? undefined : (
                        <>
                            <StatTile label="Status">
                                <Badge size="sm" variant="light" color={STATUS_BADGE_COLOR[selected.status]}>
                                    {STATUS_LABEL[selected.status]}
                                </Badge>
                            </StatTile>
                            <StatTile label="Recipients">
                                <Text size="xs" fw={700}>
                                    {selected.recipients}
                                </Text>
                            </StatTile>
                            <StatTile label="Channels">
                                <Text size="xs" fw={700}>
                                    {channelsLabel(selected)}
                                </Text>
                            </StatTile>
                        </>
                    )
                }
            >
                {selected !== null && (
                    <Stack gap="xl">
                        <Box>
                            {sectionLabel('Message')}
                            <Box
                                style={{
                                    border: '1px solid var(--bm-color-border)',
                                    borderRadius: 'var(--bm-radius)',
                                    background: 'var(--bm-color-surface)',
                                    padding: 'var(--mantine-spacing-lg)',
                                }}
                            >
                                <Text size="sm" fw={600}>
                                    {selected.title}
                                </Text>
                                <Text size="sm" mt="xs" c="dimmed" ff="monospace">
                                    {selected.templateType}
                                </Text>
                                <Group gap={6} mt="md">
                                    {selected.channels.map((c) => (
                                        <Badge key={c} size="sm" variant="light" color="gray">
                                            {CHANNEL_LABEL[c] ?? c}
                                        </Badge>
                                    ))}
                                </Group>
                            </Box>
                        </Box>
                        <Box>
                            {sectionLabel('Recipients')}
                            <Text size="sm">
                                {selected.recipients === 1 ? '1 person' : `${selected.recipients} people`}
                            </Text>
                        </Box>
                        <Box>
                            {sectionLabel('Details')}
                            <KeyValueTable
                                entries={[
                                    ['notification', selected.templateType],
                                    ['category', selected.category],
                                    ['status', STATUS_LABEL[selected.status] ?? selected.status],
                                    ['channels', channelsLabel(selected)],
                                    ['recipients', String(selected.recipients)],
                                    ['sent', formatTimestamp(selected.sentAt)],
                                    ['correlation id', selected.correlationId],
                                    ...(selected.error ? [['error', selected.error] as [string, string]] : []),
                                ]}
                                keyWidth="38%"
                            />
                        </Box>
                    </Stack>
                )}
            </DetailDrawer>
        </Box>
    );
}
